import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { FuseCardComponent } from '@fuse/components/card';
import { StatusDotComponent } from 'app/core/components/status-dot/status-dot.component';
import { BadgePipe } from 'app/core/pipes/badge.pipe';
import { ServicePipe } from 'app/core/pipes/eservice.pipe';
import { SweetAlertService } from 'app/core/services/sweet-alert.service';
import { PERMISSION_ESERVICE_VIEW } from 'app/core/types/permissions.type';
import { AuthService } from '../../../auth/auth.service';
import { EserviceService } from '../../../eservices/eservice.service';
import { EserviceDetailDialogComponent, EserviceDetailDialogData } from './dialogs/eservice-detail-dialog/eservice-detail-dialog.component';

@Component({
  selector: 'app-my-eservices',
  standalone: true,
  imports: [DatePipe, MatIconModule, FuseCardComponent, BadgePipe, ServicePipe, StatusDotComponent, MatProgressSpinnerModule],
  templateUrl: './my-eservices.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyEservicesComponent implements OnInit, OnChanges {
  private Auth = inject(AuthService);
  private Eservices = inject(EserviceService);
  private matDialog = inject(MatDialog);
  private router = inject(Router);
  private sweetAlert = inject(SweetAlertService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  error: any;
  dataLoader = false;
  dataList: any = [];
  where: any = [];
  order: any = { created_at: 'desc' };
  columns: any = ['reference', 'application_type', 'created_at', 'client_name', 'client_phone', 'status', 'unit_id', 'association_id', 'assignee_id'];
  include: any = ['association', 'unit', 'assignee'];
  profile: any;
  selectedRequest: any = {};

  @Input() fromDate: any = '';
  @Input() toDate: any = '';
  @Input() associationId: string | null = null;

  ngOnInit() {}

  ngOnChanges(_changes: SimpleChanges) {
    this.dataLoader = true;
    this.profile = this.Auth.profile();
    this.getData();
  }

  viewAll(): void {
    if (!this.Auth.canAccess([PERMISSION_ESERVICE_VIEW])) {
      this.sweetAlert.info('You do not have permission to access this page.', 'Access Denied');
      return;
    }
    this.router.navigate(['/eservices/list'], {
      queryParams: { _ref: 'dashboard', status: 'Pending', user_id: this.profile?.id },
    });
  }

  public getData() {
    this.error = null;
    this.dataList = [];
    this.dataLoader = true;
    this.where = [
      { column: 'company_id', operator: '=', value: this.profile.company_id },
      { column: 'status', operator: '=', value: 'Pending' },
      { column: 'assignee_id', operator: '=', value: this.profile.id },
    ];
    if (this.fromDate) this.where.push({ column: 'created_at', operator: '>=', value: this.fromDate });
    if (this.toDate) this.where.push({ column: 'created_at', operator: '<=', value: this.toDate });
    if (this.associationId) this.where.push({ column: 'association_id', operator: '=', value: this.associationId });

    this.Eservices.getApplications({ where: this.where, orderBy: this.order, include: this.include, columns: this.columns }, 1, 5)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => {
          this.dataList = resp.record.data;
          this.dataLoader = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.dataLoader = false;
          this.error = err.message || 'An error occurred while getting the data';
          this.cdr.markForCheck();
        },
      });
  }

  public openRequestsModal($event: any, item: any) {
    if ($event) {
      $event.stopPropagation();
    }
    if (!this.Auth.canAccess([PERMISSION_ESERVICE_VIEW])) {
      this.sweetAlert.info('You do not have permission to view this service request.', 'Access Denied');
      return;
    }
    this.selectedRequest = item;
    const onSave = new EventEmitter<any>();
    this.matDialog.open<EserviceDetailDialogComponent, EserviceDetailDialogData>(EserviceDetailDialogComponent, {
      data: { item, onSave },
    });
    onSave.subscribe(() => {
      this.matDialog.closeAll();
      this.getData();
    });
  }
}
